import axiosInstance from 'src/utils/axios'

// ----------------------------------------------------------------------
// Operadoras (taxas de custo da adquirente — PagSeguro, Stone, etc.)

export type FinanceOperatorFees = {
  credit: number
  debit: number
  pix: number
}

export type FinanceOperator = {
  _id: string
  name: string
  fees: FinanceOperatorFees
  fixedFee: number
  isDefault: boolean
  notes: string
  active: boolean
  createdAt?: string
  updatedAt?: string
}

export type FinanceOperatorPayload = {
  name: string
  fees: FinanceOperatorFees
  fixedFee: number
  isDefault: boolean
  notes?: string
  active?: boolean
}

export async function fetchOperators(): Promise<FinanceOperator[]> {
  const res = await axiosInstance.get('/finance/operators')
  return res.data.operators || []
}

export async function createOperator(payload: FinanceOperatorPayload): Promise<FinanceOperator> {
  const res = await axiosInstance.post('/finance/operators', payload)
  return res.data
}

export async function updateOperator(
  id: string,
  payload: FinanceOperatorPayload,
): Promise<FinanceOperator> {
  const res = await axiosInstance.put(`/finance/operators/${id}`, payload)
  return res.data
}

export async function deleteOperator(id: string): Promise<void> {
  await axiosInstance.delete(`/finance/operators/${id}`)
}

// ----------------------------------------------------------------------
// Custos

export type FinanceCostCategory =
  | 'machine'
  | 'logistics'
  | 'staff'
  | 'supplies'
  | 'marketing'
  | 'other'

export const COST_CATEGORY_LABELS: Record<FinanceCostCategory, string> = {
  machine: 'Máquinas',
  logistics: 'Logística',
  staff: 'Equipe',
  supplies: 'Insumos',
  marketing: 'Marketing',
  other: 'Outros',
}

export type FinanceCost = {
  _id: string
  description: string
  value: number
  date: string
  category: FinanceCostCategory
  event: { _id: string; name: string; date?: string } | null
  notes: string
  createdAt?: string
}

export type FinanceCostPayload = {
  description: string
  value: number
  date: Date | string
  category: FinanceCostCategory
  event?: string | null
  notes?: string
}

export type FinanceCostFilters = {
  event?: string // id do evento ou 'general' para custos sem evento
  category?: FinanceCostCategory
  startDate?: Date | null
  endDate?: Date | null
  page?: number
  perPage?: number
}

export type FinanceCostList = {
  costs: FinanceCost[]
  page: number
  perPage: number
  total: number
  totalPages: number
  totalValue: number
}

export async function fetchCosts(filters: FinanceCostFilters = {}): Promise<FinanceCostList> {
  const res = await axiosInstance.get('/finance/costs', {
    params: {
      event: filters.event || undefined,
      category: filters.category || undefined,
      startDate: filters.startDate ? filters.startDate.toISOString() : undefined,
      endDate: filters.endDate ? filters.endDate.toISOString() : undefined,
      page: filters.page || 1,
      perPage: filters.perPage || 10,
    },
  })
  return res.data
}

export async function createCost(payload: FinanceCostPayload): Promise<FinanceCost> {
  const res = await axiosInstance.post('/finance/costs', payload)
  return res.data
}

export async function updateCost(id: string, payload: FinanceCostPayload): Promise<FinanceCost> {
  const res = await axiosInstance.put(`/finance/costs/${id}`, payload)
  return res.data
}

export async function deleteCost(id: string): Promise<void> {
  await axiosInstance.delete(`/finance/costs/${id}`)
}

// ----------------------------------------------------------------------
// Config financeira do evento + DRE

export type BillingModel = 'tax' | 'machine'

export const BILLING_MODEL_LABELS: Record<BillingModel, string> = {
  tax: 'Por taxa',
  machine: 'Por máquina',
}

export type FinanceEventConfig = {
  billingModel?: BillingModel
  machineCount?: number
  machinePrice?: number
  machineCost?: number
  operator?: { _id: string; name: string } | string | null
}

export type FinanceEvent = {
  _id: string
  name: string
  date?: string
  local?: string
  active: boolean
  finance?: FinanceEventConfig
  audit?: { contractedTerminals?: number }
}

export async function fetchFinanceEvents(search?: string): Promise<FinanceEvent[]> {
  const res = await axiosInstance.get('/finance/events', {
    params: { search: search || undefined },
  })
  return res.data.events || []
}

export type FinanceEventConfigPayload = {
  billingModel: BillingModel
  machineCount: number
  machinePrice: number
  machineCost: number
  operator: string | null
}

export async function updateEventFinanceConfig(
  eventId: string,
  payload: FinanceEventConfigPayload,
): Promise<{ eventId: string; finance: FinanceEventConfig }> {
  const res = await axiosInstance.patch(`/finance/event/${eventId}/config`, payload)
  return res.data
}

export type DrePaymentSlot = { gross: number; tax: number; count: number }

export type DrePaymentMap = Record<'money' | 'credit' | 'debit' | 'pix' | 'card', DrePaymentSlot>

export type DreEventRow = {
  eventId: string
  name: string
  date?: string
  local: string
  active: boolean
  billingModel: BillingModel
  operator: { _id: string; name: string } | null
  operatorIsDefault: boolean
  machineCount: number
  machinePrice: number
  machineCostUnit: number
  payments: DrePaymentMap
  gross: number
  taxRevenue: number
  machineRevenue: number
  revenue: number
  costs: {
    acquirer: number
    machines: number
    entries: number
    total: number
  }
  costEntries: Array<{
    _id: string
    description: string
    value: number
    date: string
    category: FinanceCostCategory
  }>
  result: number
  margin: number
}

export type DreTotals = {
  gross: number
  taxRevenue: number
  machineRevenue: number
  revenue: number
  acquirerCost: number
  machineCost: number
  entryCosts: number
  generalCosts: number
  totalCosts: number
  result: number
  margin: number
}

export type DreData = {
  scope: 'global' | 'selected'
  eventCount: number
  period: { start: string | null; end: string | null }
  defaultOperator: { _id: string; name: string } | null
  events: DreEventRow[]
  generalCosts: { total: number; items: FinanceCost[] }
  totals: DreTotals
}

export type DreParams = {
  events?: string[]
  // Quando true, consolida todos os eventos com atividade financeira (ignora `events`).
  all?: boolean
  startDate?: Date | null
  endDate?: Date | null
  includeGeneralCosts?: boolean
}

export async function fetchDre(params: DreParams): Promise<DreData> {
  const res = await axiosInstance.get('/finance/dre', {
    params: {
      all: params.all ? 'true' : undefined,
      events: params.all ? undefined : (params.events || []).join(','),
      startDate: params.startDate ? params.startDate.toISOString() : undefined,
      endDate: params.endDate ? params.endDate.toISOString() : undefined,
      includeGeneralCosts: params.includeGeneralCosts === false ? 'false' : 'true',
    },
  })
  return res.data
}
